// src/visualizations/LeadershipOrgChart.jsx
// ============================================================
// Adaptive Product — Leadership Org Chart
// Standalone visualization, no auth, no AppShell.
// Route: /viz/leadership-org-chart
//
// One-page screen view: 12 leaders across 4 swim lanes
//   (Go-to-Market, Capital & Fundraising, Product & Tech,
//    People & Ops)
// + Founder Support note + inline Execution Model strip.
//
// PDF EXPORT (2 pages, landscape Letter):
//   Page 1: Org chart (header, lanes, founder note)
//   Page 2: Expanded Execution Model
//
// We load html2canvas (1.4.1) and jsPDF (2.5.1) directly from
// cdnjs and capture each page section as its own canvas, then
// build the PDF manually with jsPDF.addImage. This avoids
// html2pdf.js's automatic slicer which produced overflow when
// the captured DOM height didn't align cleanly with the page
// break — and it gives us direct access to both globals, which
// the html2pdf.js bundle keeps internal.
//
// HTML2CANVAS COMPATIBILITY NOTES:
// - All multi-column layouts use flexbox, NOT CSS Grid.
//   html2canvas v1.4 has only partial Grid support.
// - The captured elements (page1Ref, page2Ref) live inside the
//   wrapper that owns the <style> tag, so CSS rules apply during
//   the cloned render.
// - The .exporting class lives on the wrapper, so descendant rules
//   like ".org-chart-page.exporting .pdf-page-2 { display: block }"
//   match at capture time.
// - document.fonts.ready is awaited before capture so Montserrat
//   is fully loaded.
// ============================================================

import React, { useEffect, useRef, useState } from "react";

const HTML2CANVAS_CDN =
  "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
const JSPDF_CDN =
  "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');

.org-chart-page {
  --navy: #18365E;
  --coral: #FF6E4C;
  --bg: #ffffff;
  --soft: #F5F7FA;
  --border: #e5e7eb;
  --muted: #717182;
  --text: #1a2433;
  --open: #b9c2d1;
  background: var(--bg);
  color: var(--text);
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  padding: 24px 28px;
  min-height: 100vh;
}

.org-chart-page *,
.org-chart-page *::before,
.org-chart-page *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.org-chart-page .container {
  max-width: 1340px;
  margin: 0 auto;
}

/* ============ EXPORT BUTTON ============ */
.org-chart-page .export-btn {
  position: fixed;
  top: 20px;
  right: 24px;
  background: var(--navy);
  color: white;
  border: none;
  border-radius: 999px;
  padding: 10px 18px 10px 14px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(24, 54, 94, 0.25);
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  z-index: 100;
}
.org-chart-page .export-btn:hover:not(:disabled) {
  background: #214d80;
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(24, 54, 94, 0.32);
}
.org-chart-page .export-btn:active { transform: translateY(0); }
.org-chart-page .export-btn:disabled {
  opacity: 0.7;
  cursor: wait;
  transform: none;
}
.org-chart-page .export-btn svg { flex-shrink: 0; }

/* ============ HEADER ============ */
.org-chart-page .header { text-align: center; margin-bottom: 22px; }
.org-chart-page .eyebrow {
  text-transform: uppercase;
  letter-spacing: 2.2px;
  font-size: 10px;
  font-weight: 700;
  color: var(--coral);
  margin-bottom: 8px;
}
.org-chart-page h1 {
  font-size: 26px;
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.4px;
  line-height: 1.15;
}
.org-chart-page .subtitle {
  color: var(--muted);
  font-size: 12px;
  margin-top: 8px;
  max-width: 780px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  font-weight: 500;
}

/* ============ LANES (FLEX, not Grid — html2canvas compatibility) ============ */
.org-chart-page .lanes {
  display: flex;
  gap: 14px;
  align-items: stretch;
  flex-wrap: wrap;
}
.org-chart-page .lane {
  flex: 1 1 calc(25% - 11px);
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.org-chart-page .lane-header {
  background: var(--navy);
  color: white;
  border-radius: 8px;
  padding: 9px 12px 8px;
  text-align: center;
  box-shadow: 0 3px 10px rgba(24, 54, 94, 0.12);
}
.org-chart-page .lane-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  line-height: 1.15;
}
.org-chart-page .lane-sub {
  font-size: 9px;
  opacity: 0.7;
  margin-top: 2px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

/* ============ CARDS ============ */
.org-chart-page .card {
  background: white;
  border: 1px solid var(--border);
  border-radius: 9px;
  padding: 9px 12px 8px;
  display: flex;
  flex-direction: column;
}
.org-chart-page .card.open-role {
  border-style: dashed;
  border-color: var(--open);
  background: #fafbfc;
}
.org-chart-page .badge {
  display: inline-flex;
  align-items: center;
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 999px;
  margin-bottom: 6px;
  align-self: flex-start;
  background: white;
  color: var(--navy);
  border: 1.2px dashed var(--navy);
}
.org-chart-page .badge::before {
  content: '+';
  margin-right: 4px;
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
}
.org-chart-page .card-name {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 2px;
  line-height: 1.2;
  letter-spacing: -0.1px;
}
.org-chart-page .card-title {
  font-size: 10.5px;
  color: var(--muted);
  font-weight: 600;
  margin-bottom: 5px;
  line-height: 1.3;
}
.org-chart-page .card-section-label {
  font-size: 8.5px;
  font-weight: 700;
  color: var(--coral);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 3px;
}
.org-chart-page .card-owns {
  font-size: 10.5px;
  color: #2a3a4f;
  line-height: 1.4;
}
.org-chart-page .card-note {
  margin-top: 6px;
  padding-top: 5px;
  border-top: 1px dashed var(--border);
  font-size: 9.5px;
  color: var(--muted);
  font-style: italic;
  line-height: 1.35;
}

/* ============ FOUNDER SUPPORT ============ */
.org-chart-page .founder-note {
  margin-top: 14px;
  background: white;
  border: 1px solid var(--border);
  border-left: 3px solid var(--coral);
  border-radius: 8px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.org-chart-page .founder-tag {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--coral);
  flex-shrink: 0;
}
.org-chart-page .founder-text {
  font-size: 11.5px;
  color: #2a3a4f;
  line-height: 1.45;
  font-weight: 500;
}
.org-chart-page .founder-text strong {
  color: var(--navy);
  font-weight: 700;
}

/* ============ EXECUTION MODEL — INLINE (SCREEN ONLY, FLEX) ============ */
.org-chart-page .execution-inline {
  margin-top: 14px;
  background: var(--soft);
  border-radius: 10px;
  padding: 14px 18px;
  border: 1px solid var(--border);
}
.org-chart-page .execution-inline-row {
  display: flex;
  gap: 18px;
  align-items: stretch;
  flex-wrap: wrap;
}
.org-chart-page .execution-inline-label {
  flex: 0 0 auto;
  border-right: 1px solid var(--border);
  padding-right: 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.org-chart-page .execution-inline-eyebrow {
  text-transform: uppercase;
  letter-spacing: 1.4px;
  font-size: 9px;
  font-weight: 700;
  color: var(--coral);
  margin-bottom: 2px;
}
.org-chart-page .execution-inline-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.2px;
  line-height: 1.15;
}
.org-chart-page .principle-inline {
  flex: 1 1 0;
  min-width: 180px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.org-chart-page .principle-inline-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--coral);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}
.org-chart-page .principle-inline-body { min-width: 0; flex: 1 1 auto; }
.org-chart-page .principle-inline-title {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 1px;
  line-height: 1.2;
  letter-spacing: -0.05px;
}
.org-chart-page .principle-inline-desc {
  font-size: 10px;
  color: var(--muted);
  line-height: 1.4;
  font-weight: 500;
}

/* ============ EXECUTION MODEL — FULL (PDF PAGE 2, FLEX) ============ */
.org-chart-page .execution-full {
  background: var(--soft);
  border-radius: 14px;
  padding: 36px 32px 32px;
  border: 1px solid var(--border);
}
.org-chart-page .execution-full-header { text-align: center; margin-bottom: 28px; }
.org-chart-page .execution-full-eyebrow {
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 10px;
  font-weight: 700;
  color: var(--coral);
  margin-bottom: 8px;
}
.org-chart-page .execution-full-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 8px;
  letter-spacing: -0.3px;
  line-height: 1.15;
}
.org-chart-page .execution-full-sub {
  font-size: 13px;
  color: var(--muted);
  font-weight: 500;
  max-width: 640px;
  margin: 0 auto;
  line-height: 1.5;
}
.org-chart-page .principles-full {
  display: flex;
  gap: 20px;
  align-items: stretch;
  flex-wrap: wrap;
}
.org-chart-page .principle-full {
  flex: 1 1 0;
  min-width: 220px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 28px 24px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.org-chart-page .principle-full-num {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--coral);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  margin: 0 auto 16px;
  box-shadow: 0 4px 10px rgba(255, 110, 76, 0.25);
}
.org-chart-page .principle-full-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 10px;
  letter-spacing: -0.1px;
}
.org-chart-page .principle-full-desc {
  font-size: 12px;
  color: var(--muted);
  line-height: 1.55;
  font-weight: 500;
}

/* ============ PAGE SECTIONS (capture targets) ============ */
.org-chart-page .pdf-page-1 {
  /* page 1 is just the chart content; visible on screen */
  background: white;
  padding: 4px;
}
.org-chart-page .pdf-page-2 {
  /* page 2 is hidden on screen; only revealed during PDF export
     so html2canvas can capture it from the live DOM */
  display: none;
  background: white;
  padding: 4px;
}

/* ============ EXPORT MODE ============ */
.org-chart-page.exporting {
  /* Reset wrapper padding during PDF capture so the canvas isn't
     padded; jsPDF margins handle outer space. */
  padding: 0;
  min-height: 0;
}
.org-chart-page.exporting .export-btn { display: none; }
.org-chart-page.exporting .execution-inline { display: none; }
.org-chart-page.exporting .footer { display: none; }
.org-chart-page.exporting .pdf-page-2 { display: block; }

/* PDF export reflows the 4 lanes into a 2x2 grid. With landscape
   orientation, each lane gets more horizontal width, so cards
   render with fewer wrapped lines and the captured canvas fills
   the page area cleanly without the awkward whitespace that the
   4-up-on-landscape and 2-up-on-portrait variants produced.
   Lanes wrap naturally because .lanes already has flex-wrap: wrap. */
.org-chart-page.exporting .lane {
  flex: 1 1 calc(50% - 7px);
  min-width: 0;
}

/* ============ FOOTER ============ */
.org-chart-page .footer {
  text-align: center;
  margin-top: 14px;
  font-size: 9.5px;
  color: var(--muted);
  letter-spacing: 1.4px;
  text-transform: uppercase;
  font-weight: 600;
}
.org-chart-page .footer span { color: var(--coral); }

/* ============ RESPONSIVE ============ */
@media (max-width: 1100px) {
  .org-chart-page .lanes { gap: 18px; }
  .org-chart-page .lane { flex: 1 1 calc(50% - 9px); }
  .org-chart-page .principle-full { flex: 1 1 calc(50% - 10px); }
}
@media (max-width: 700px) {
  .org-chart-page .lane { flex: 1 1 100%; }
  .org-chart-page .principle-full { flex: 1 1 100%; }
  .org-chart-page .principle-inline { flex: 1 1 100%; }
  .org-chart-page .execution-inline-label {
    flex: 1 1 100%;
    border-right: none;
    border-bottom: 1px solid var(--border);
    padding-right: 0;
    padding-bottom: 10px;
  }
  .org-chart-page h1 { font-size: 22px; }
  .org-chart-page .export-btn { top: 14px; right: 14px; padding: 8px 14px; font-size: 11px; }
}

@media print {
  .org-chart-page .export-btn { display: none; }
}
`;

export default function LeadershipOrgChart() {
  const [isExporting, setIsExporting] = useState(false);

  function librariesPresent() {
    if (typeof window === "undefined") return false;
    const hasCanvas = Boolean(window.html2canvas);
    const hasPdf = Boolean(
      (window.jspdf && window.jspdf.jsPDF) || window.jsPDF
    );
    return hasCanvas && hasPdf;
  }

  const [scriptReady, setScriptReady] = useState(librariesPresent());
  const rootRef = useRef(null);
  const page1Ref = useRef(null);
  const page2Ref = useRef(null);

  // Load html2canvas and jsPDF directly from CDN. We deliberately do NOT
  // use the html2pdf.js bundle wrapper — it keeps html2canvas and jsPDF
  // internal so they aren't accessible as window globals, which is what
  // we need for the manual two-pass capture below.
  useEffect(() => {
    if (typeof window === "undefined") return;

    function ensureScript(src, isLoaded) {
      if (isLoaded()) return Promise.resolve();

      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        // Already injected (e.g. by a previous mount) — wait for global.
        return new Promise((resolve, reject) => {
          let elapsed = 0;
          const interval = setInterval(() => {
            if (isLoaded()) {
              clearInterval(interval);
              resolve();
            } else if ((elapsed += 100) > 30000) {
              clearInterval(interval);
              reject(new Error(`Timed out waiting for ${src}`));
            }
          }, 100);
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.crossOrigin = "anonymous";
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });
    }

    Promise.all([
      ensureScript(HTML2CANVAS_CDN, () => Boolean(window.html2canvas)),
      ensureScript(
        JSPDF_CDN,
        () => Boolean((window.jspdf && window.jspdf.jsPDF) || window.jsPDF)
      ),
    ])
      .then(() => setScriptReady(true))
      .catch((err) => {
        console.error("Failed to load PDF export libraries:", err);
      });
  }, []);

  /**
   * Place a captured canvas onto a PDF page, scaled to fit while
   * preserving aspect ratio and centered both axes.
   */
  function placeCanvasOnPage(pdf, canvas, isFirstPage) {
    if (!isFirstPage) pdf.addPage();

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 10; // mm
    const availW = pageW - 2 * margin;
    const availH = pageH - 2 * margin;

    const canvasRatio = canvas.width / canvas.height;
    const availRatio = availW / availH;

    let imgW, imgH;
    if (canvasRatio > availRatio) {
      // Canvas is wider than available area — fit to width.
      imgW = availW;
      imgH = imgW / canvasRatio;
    } else {
      // Canvas is taller — fit to height.
      imgH = availH;
      imgW = imgH * canvasRatio;
    }

    const x = margin + (availW - imgW) / 2; // center horizontally
    const y = margin; // top-align vertically (whitespace falls below, like a doc)

    const imgData = canvas.toDataURL("image/jpeg", 0.98);
    pdf.addImage(imgData, "JPEG", x, y, imgW, imgH);
  }

  async function handleExport() {
    const html2canvas = window.html2canvas;
    // jsPDF v2+ ships under window.jspdf.jsPDF; older builds also expose window.jsPDF.
    const jsPDFCtor =
      (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;

    if (!html2canvas || !jsPDFCtor) {
      window.alert(
        "PDF library failed to load. Check your internet connection and try again."
      );
      return;
    }
    if (!page1Ref.current || !page2Ref.current) return;

    setIsExporting(true);

    // Yield two animation frames so React's re-render with the
    // .exporting class flushes (revealing pdf-page-2, hiding inline exec).
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);

    // Wait for fonts so Montserrat renders in the canvas.
    if (
      typeof document !== "undefined" &&
      document.fonts &&
      document.fonts.ready &&
      typeof document.fonts.ready.then === "function"
    ) {
      try {
        await document.fonts.ready;
      } catch (_) {
        /* ignore */
      }
    }

    try {
      const captureOpts = {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        // Skip the export button if it ever ends up inside a captured tree.
        ignoreElements: (el) =>
          el.classList && el.classList.contains("export-btn"),
      };

      // Capture page 1 (chart content) and page 2 (execution model)
      // as separate canvases. Each will be placed on its own PDF page.
      const canvas1 = await html2canvas(page1Ref.current, captureOpts);
      const canvas2 = await html2canvas(page2Ref.current, captureOpts);

      const pdf = new jsPDFCtor({
        orientation: "landscape",
        unit: "mm",
        format: "letter",
      });

      placeCanvasOnPage(pdf, canvas1, true);
      placeCanvasOnPage(pdf, canvas2, false);

      pdf.save("adaptive-product-leadership-org-chart.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      window.alert("PDF export failed. See browser console for details.");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div
      className={`org-chart-page${isExporting ? " exporting" : ""}`}
      ref={rootRef}
    >
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      <button
        className="export-btn"
        onClick={handleExport}
        disabled={isExporting || !scriptReady}
        aria-label="Export PDF"
        type="button"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span>{isExporting ? "Generating…" : "Export PDF"}</span>
      </button>

      <div className="container">
        {/* ===========================================================
            PAGE 1 — Chart content (header, lanes, founder note,
            inline execution strip on screen). The inline execution
            is hidden during PDF capture via .exporting CSS.
            =========================================================== */}
        <div className="pdf-page-1" ref={page1Ref}>
          {/* HEADER */}
          <div className="header">
            <div className="eyebrow">Adaptive Product · Leadership</div>
            <h1>Senior, Domain-Owned Execution Leadership</h1>
            <div className="subtitle">
              A senior leadership team owns and drives go-to-market,
              fundraising, product, financial, and operating priorities — each
              domain led with clear accountability.
            </div>
          </div>

          {/* LANES */}
          <div className="lanes">
            {/* LANE 1: GO-TO-MARKET */}
            <div className="lane">
              <div className="lane-header">
                <div className="lane-title">Go-to-Market</div>
                <div className="lane-sub">Engagement · Pilots · Partnerships</div>
              </div>

              <div className="card">
                <div className="card-name">Matt Frankel</div>
                <div className="card-title">
                  Head of Services & Health System Engagement
                </div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Services organization, health system engagement, Health
                  System Proof Points epic, Health System Paid Pilot epic.
                </div>
              </div>

              <div className="card">
                <div className="card-name">Mwenge Ngona</div>
                <div className="card-title">Chief Marketing Officer</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Market positioning, engagement campaigns, campaign support
                  for highest-priority initiatives.
                </div>
              </div>

              <div className="card">
                <div className="card-name">Tom Fervoy</div>
                <div className="card-title">SVP Business Development</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Strategic partnerships, strategic investor strategy, strategic
                  investor engagement.
                </div>
              </div>
            </div>

            {/* LANE 2: CAPITAL & FUNDRAISING */}
            <div className="lane">
              <div className="lane-header">
                <div className="lane-title">Capital & Fundraising</div>
                <div className="lane-sub">
                  Financial · Investor · Capital Plan
                </div>
              </div>

              <div className="card">
                <div className="card-name">Brad Silcox</div>
                <div className="card-title">
                  Financial Strategy & Organizational Growth Lead
                </div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Capital & Fundraising workstream lead. Financial model,
                  financial management, capital planning, economic
                  narrative; oversight of fundraising execution.
                </div>
              </div>

              <div className="card">
                <div className="card-name">Steve Green</div>
                <div className="card-title">Head of Fundraising</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  End-to-end fundraising execution, investor pipeline,
                  fundraising work effort.
                </div>
              </div>

              <div className="card">
                <div className="card-name">Dave Tushingham</div>
                <div className="card-title">Fundraising Advisor</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Investor introductions; capital partner engagement;
                  fundraising support for the raise.
                </div>
              </div>

              <div className="card">
                <div className="card-name">John Mathews</div>
                <div className="card-title">
                  Strategic Growth & Fundraising Advisor
                </div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Fundraising plan support and investor strategy support.
                </div>
              </div>
            </div>

            {/* LANE 3: PRODUCT & TECHNOLOGY */}
            <div className="lane">
              <div className="lane-header">
                <div className="lane-title">Product & Technology</div>
                <div className="lane-sub">Architecture · Data · Delivery</div>
              </div>

              <div className="card">
                <div className="card-name">Rahul Khobe</div>
                <div className="card-title">Technical Architecture Advisor</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Architecture direction, developer coordination, data science
                  execution support.
                </div>
              </div>

              <div className="card">
                <div className="card-name">Paul Fervoy</div>
                <div className="card-title">Market & Technical Advisor</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  MVP optimization, data source strategy, data science
                  strategy, short- and long-term data strategy.
                </div>
              </div>

              <div className="card">
                <div className="card-name">Dhirg Rasjeshike</div>
                <div className="card-title">Project Management / PDLC Lead</div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Sprint cadence, PDLC discipline, execution tracking.
                </div>
              </div>
            </div>

            {/* LANE 4: PEOPLE & OPERATIONS */}
            <div className="lane">
              <div className="lane-header">
                <div className="lane-title">People & Operations</div>
                <div className="lane-sub">Org Strategy · Operating Cadence</div>
              </div>

              <div className="card">
                <div className="card-name">Dan Hardke</div>
                <div className="card-title">
                  Chief People & Organizational Strategy Lead
                </div>
                <div className="card-section-label">Owns</div>
                <div className="card-owns">
                  Operator agreements, equity agreements, startup incentive
                  plan, COO recruiting.
                </div>
              </div>

              <div className="card open-role">
                <span className="badge">Open Role</span>
                <div className="card-name">Chief Operating Officer</div>
                <div className="card-section-label">Will own</div>
                <div className="card-owns">
                  Day-to-day operations, cross-functional execution, operating
                  cadence.
                </div>
                <div className="card-note">
                  Active recruiting led by Dan Hardke.
                </div>
              </div>
            </div>
          </div>

          {/* FOUNDER SUPPORT */}
          <div className="founder-note">
            <span className="founder-tag">Founder Support</span>
            <span className="founder-text">
              <strong>Chris Donovan</strong> serves the team in a supporting
              role — moving to the next highest-priority work in whichever
              domain needs it most.
            </span>
          </div>

          {/* EXECUTION MODEL — INLINE (screen view; hidden during export) */}
          <div className="execution-inline">
            <div className="execution-inline-row">
              <div className="execution-inline-label">
                <div className="execution-inline-eyebrow">How we execute</div>
                <div className="execution-inline-title">Execution Model</div>
              </div>
              <div className="principle-inline">
                <div className="principle-inline-num">1</div>
                <div className="principle-inline-body">
                  <div className="principle-inline-title">Domain Ownership</div>
                  <div className="principle-inline-desc">
                    Each leader owns clear scope and outcomes — every priority
                    has a single throat to choke.
                  </div>
                </div>
              </div>
              <div className="principle-inline">
                <div className="principle-inline-num">2</div>
                <div className="principle-inline-body">
                  <div className="principle-inline-title">
                    Lightweight Coordination
                  </div>
                  <div className="principle-inline-desc">
                    Coordination is intentional and minimal. Leaders sync
                    where it matters and stay out of each other's way
                    otherwise.
                  </div>
                </div>
              </div>
              <div className="principle-inline">
                <div className="principle-inline-num">3</div>
                <div className="principle-inline-body">
                  <div className="principle-inline-title">
                    Sprint-Based Execution Discipline
                  </div>
                  <div className="principle-inline-desc">
                    Work moves in time-boxed sprints with visible cadence and
                    weekly tracking against epics.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===========================================================
            PAGE 2 — Expanded Execution Model. Hidden on screen,
            revealed only during PDF capture via .exporting CSS.
            =========================================================== */}
        <div className="pdf-page-2" ref={page2Ref}>
          <div className="execution-full">
            <div className="execution-full-header">
              <div className="execution-full-eyebrow">How we execute</div>
              <div className="execution-full-title">Execution Model</div>
              <div className="execution-full-sub">
                Three principles that govern how leadership coordinates and
                delivers across domains.
              </div>
            </div>
            <div className="principles-full">
              <div className="principle-full">
                <div className="principle-full-num">1</div>
                <div className="principle-full-title">Domain Ownership</div>
                <div className="principle-full-desc">
                  Each leader owns clear scope and outcomes. No diffuse
                  accountability — every priority has a single throat to
                  choke.
                </div>
              </div>
              <div className="principle-full">
                <div className="principle-full-num">2</div>
                <div className="principle-full-title">
                  Lightweight Coordination
                </div>
                <div className="principle-full-desc">
                  Coordination is intentional and minimal. Leaders sync where
                  it matters and stay out of each other's way everywhere
                  else.
                </div>
              </div>
              <div className="principle-full">
                <div className="principle-full-num">3</div>
                <div className="principle-full-title">
                  Sprint-Based Execution Discipline
                </div>
                <div className="principle-full-desc">
                  Work moves in time-boxed sprints with visible cadence,
                  clear deliverables, and weekly tracking against epics.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer">
          Adaptive Product <span>·</span> Leadership Org Chart{" "}
          <span>·</span> 2026
        </div>
      </div>
    </div>
  );
}
